import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'
import { CircleAlert } from 'lucide-react'
import { toast } from 'sonner'
import { DataTable, selectionColumn } from '@/components/ui/data-table'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { BulkActionBar } from './BulkActionBar'
import { NameCell } from './TableCells'
import { statMetricColumns } from './statsColumns'
import { StatFilterBar, useStatThresholds } from './StatThresholdFilter'
import { useStatLabel } from '../hooks/useStatLabel'
import {
  ADVERTISING_STAT_METRICS,
  useGroupedStats,
  type GroupedStatsRow,
  type StatsGroup,
} from '../hooks/useGroupedStats'
import { changeAdUnitTarget, type AdUnitTargetResult } from '@/services/targeting/targeting'
import type { BreakdownTableProps } from './TargetingTotalsTable'

type AdUnitStatsRow = GroupedStatsRow<StatsGroup>

/** A change worked out and waiting for the user to confirm it. */
interface PendingChange extends AdUnitTargetResult {
  mode: 'include' | 'exclude'
  count: number
  /** Including into an empty target narrows delivery to just these. */
  narrows: boolean
}

/**
 * Delivery per ad unit — every one the line item served on, not a top few, so
 * the weakest can be found by sorting or filtering. With `adUnitTargeting`
 * the selected ad units can be included in or excluded from targeting.
 */
export function AdUnitStatsTable({ query, range, maxHeight, adUnitTargeting }: BreakdownTableProps) {
  const { t } = useTranslation()
  const statLabel = useStatLabel()
  // No topN: an "all others" bucket would hide exactly the ad units worth finding.
  const allQuery = useMemo(() => ({ ...query, topN: undefined }), [query])
  const { rows, isLoading, error } = useGroupedStats('AD_UNIT', 'adUnit', allQuery, range)
  const [selection, setSelection] = useState<AdUnitStatsRow[]>([])
  const [pending, setPending] = useState<PendingChange | null>(null)

  const targeting = adUnitTargeting?.targeting
  const editable = !!adUnitTargeting && !adUnitTargeting.lockedReason

  const { thresholds, setThresholds, filtered } = useStatThresholds(
    rows,
    (row) => row.stats,
    ADVERTISING_STAT_METRICS
  )

  // Where each ad unit stands in the targeting now, for the status column.
  const targetStatus = useMemo(() => {
    const target = targeting?.adUnitTarget
    const status = target?.negated ? 'excluded' : 'targeted'
    return new Map((target?.adUnits ?? []).map((unit) => [unit.id, status]))
  }, [targeting])

  const columns = useMemo(
    (): ColumnDef<AdUnitStatsRow, unknown>[] => [
      ...(editable ? [selectionColumn<AdUnitStatsRow>()] : []),
      {
        id: 'adUnit',
        header: t('adUnits.title'),
        size: 220,
        meta: { label: t('adUnits.title') },
        accessorFn: (row) => row.group.name ?? row.group.id,
        cell: ({ row }) => <NameCell name={row.original.group.name ?? row.original.group.id} />,
      },
      ...(adUnitTargeting
        ? [
            {
              id: 'targeting',
              header: t('stats.adUnitTargeting.column'),
              size: 110,
              meta: { label: t('stats.adUnitTargeting.column') },
              accessorFn: (row: AdUnitStatsRow) => targetStatus.get(row.group.id) ?? '',
              cell: ({ row }: { row: { original: AdUnitStatsRow } }) => {
                const status = targetStatus.get(row.original.group.id)
                if (!status) return null
                return (
                  <Badge variant="outline" className="px-1.5 font-normal">
                    {t(`stats.adUnitTargeting.${status}`)}
                  </Badge>
                )
              },
            } satisfies ColumnDef<AdUnitStatsRow, unknown>,
          ]
        : []),
      ...statMetricColumns<AdUnitStatsRow>({
        metrics: ADVERTISING_STAT_METRICS,
        statsFor: (row) => row.stats,
        label: statLabel,
      }),
    ],
    [t, statLabel, editable, adUnitTargeting, targetStatus]
  )

  function prepare(mode: 'include' | 'exclude') {
    if (!targeting) return
    const adUnits = selection.map((row) => ({ id: row.group.id, name: row.group.name }))
    const result = changeAdUnitTarget(targeting, adUnits, mode)
    if (result.change === 'unchanged') {
      toast.info(t(`stats.adUnitTargeting.unchanged.${mode}`))
      return
    }
    if (result.change === 'wouldEmpty') {
      toast.error(t('stats.adUnitTargeting.wouldEmpty'))
      return
    }
    const narrows = result.change === 'added' && !targeting.adUnitTarget?.adUnits?.length
    setPending({ ...result, mode, count: adUnits.length, narrows })
  }

  function confirm() {
    if (!pending || !adUnitTargeting) return
    adUnitTargeting.onChange(pending.targeting)
    setPending(null)
    setSelection([])
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 px-(--card-spacing) py-4 text-destructive">
        <CircleAlert className="size-4" />
        <span className="text-sm">{error.message}</span>
      </div>
    )
  }

  return (
    <>
      <StatFilterBar
        metrics={ADVERTISING_STAT_METRICS}
        value={thresholds}
        onChange={setThresholds}
        shown={filtered.length}
        total={rows.length}
      >
        {adUnitTargeting?.lockedReason}
      </StatFilterBar>
      {editable && (
        <BulkActionBar
          count={selection.length}
          onClear={() => setSelection([])}
          loading={adUnitTargeting.saving}
          actions={[
            {
              label: t('stats.adUnitTargeting.include'),
              icon: 'plus',
              onClick: () => prepare('include'),
            },
            {
              label: t('stats.adUnitTargeting.exclude'),
              icon: 'ban',
              onClick: () => prepare('exclude'),
            },
          ]}
        />
      )}
      <DataTable
        columns={columns}
        data={filtered}
        loading={isLoading}
        getRowId={(row) => row.group.id}
        emptyMessage={t('stats.noAdUnitStats')}
        columnsKey="breakdownAdUnits"
        maxHeight={maxHeight}
        inset
        {...(editable ? { selection, onSelectionChange: setSelection } : {})}
      />

      <AlertDialog open={!!pending} onOpenChange={(open) => !open && setPending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pending &&
                t(`stats.adUnitTargeting.confirmTitle.${pending.mode}`, { count: pending.count })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pending &&
                t(
                  `stats.adUnitTargeting.confirm.${pending.narrows ? 'narrows' : pending.change}`,
                  { count: pending.count }
                )}{' '}
              {t('stats.adUnitTargeting.savesNow')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirm}>
              {pending && t(`stats.adUnitTargeting.${pending.mode}`)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
